import type { NextRouter } from 'next/router';

import {
    type RegionAwareNavigationOptions,
    resolveRegionAwareNavigationTarget,
} from './resolveRegionAwareNavigationTarget';

type NavigateFn = NextRouter['push'] | NextRouter['replace'];

export type NavigateRegionAwareRouteParams = {
    navigate: NavigateFn;
    displayUrl: string;
    getCanonicalRoute: (href: string) => string;
    getRouteWithRegion?: (href: string) => string;
    options?: RegionAwareNavigationOptions;
    shallowRoute?: Pick<NextRouter, 'pathname' | 'query'>;
};

/**
 * Applies {@link resolveRegionAwareNavigationTarget} and delegates to `router.push` or
 * `router.replace`. Shared by React hooks and non-React callers (e.g. tn-flows entities).
 */
export const navigateRegionAwareRoute = ({
    navigate,
    displayUrl,
    getCanonicalRoute,
    getRouteWithRegion,
    options,
    shallowRoute,
}: NavigateRegionAwareRouteParams): ReturnType<NavigateFn> => {
    const { href, as } = resolveRegionAwareNavigationTarget({
        url: displayUrl,
        options,
        getCanonicalRoute,
        getRouteWithRegion,
        shallowRoute: options?.shallow ? shallowRoute : undefined,
    });

    if (as !== undefined) {
        return options !== undefined ? navigate(href, as, options) : navigate(href, as);
    }

    return options !== undefined ? navigate(href, undefined, options) : navigate(href);
};
